import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { SiteIdCodec } from "@visioni/a3s-schema/codecs";
import { FEATURE_NAME } from "../constants";
import { PutReportEmailDeliveryBody, ReportEmailDeliveryState } from "./report-email-delivery";

export const ScopedReportEmailDeliveryPath = z.object({ siteId: SiteIdCodec });
export type ScopedReportEmailDeliveryPath = z.output<typeof ScopedReportEmailDeliveryPath>;

/**
 * `roadsense/email-delivery/sites/{siteId}`: the calling user's own RoadSense
 * report email delivery for one site, open to anyone with access in the
 * tenant. Acts on the caller's row only, or the impersonated user's while a
 * support user impersonates. Not `roadsense/sites/{siteId}/email-delivery`,
 * which is `{type}/sites/{siteId}/email-delivery` with `type` = `roadsense`.
 */
export const scopedReportEmailDeliveryResource = defineResource({
  feature: FEATURE_NAME,
  resource: "roadsense/email-delivery/sites/{siteId}",
  resourcePathSchema: ScopedReportEmailDeliveryPath,
  scope: "tenant",
});

/** The caller's delivery. No scope check, so a user who lost access still sees it. */
export const getScopedReportEmailDeliveryEndpoint = defineEndpoint(scopedReportEmailDeliveryResource, {
  responseSchema: ReportEmailDeliveryState,
});

/**
 * Creates or replaces the caller's delivery and returns the same shape as GET.
 * 404 unless the site has at least one movement in the caller's scope in an
 * open RoadSense report; 409 at the per-user cap.
 */
export const putScopedReportEmailDeliveryEndpoint = defineEndpoint(scopedReportEmailDeliveryResource, {
  method: "PUT",
  bodySchema: PutReportEmailDeliveryBody,
  responseSchema: ReportEmailDeliveryState,
});

/** Stops the caller's delivery. No scope check; answers `204 No Content`, whether or not one existed. */
export const deleteScopedReportEmailDeliveryEndpoint = defineEndpoint(scopedReportEmailDeliveryResource, {
  method: "DELETE",
  responseSchema: z.undefined(),
});
